import { useEffect, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  Warning20Regular,
  CheckmarkCircle20Filled,
  Filter20Regular,
  ArrowLeft20Regular,
  ArrowRight20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  ConflictKind,
  ImportKind,
  PreviewRow,
  RowDecision,
  RowDecisionAction,
  formatReason,
  importService,
} from "../../services/importService";
import { ImportMergeDrawer } from "./ImportMergeDrawer";

const useStyles = makeStyles({
  filterBar: {
    display: "flex",
    columnGap: "8px",
    marginBottom: "16px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  filterIcon: {
    color: BRAND.muted,
    marginRight: "4px",
  },
  pill: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    ":hover": { backgroundColor: BRAND.bg },
  },
  pillActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.ink },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.88rem",
  },
  th: {
    textAlign: "left",
    fontWeight: 600,
    color: "#5d5664",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.bg,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  td: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    verticalAlign: "top",
  },
  reasons: {
    color: BRAND.muted,
    fontSize: "0.78rem",
    marginTop: "4px",
  },
  badgeBase: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    paddingLeft: "8px",
    paddingRight: "8px",
    paddingTop: "3px",
    paddingBottom: "3px",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    ...shorthands.borderRadius("4px"),
  },
  badgeHard: { backgroundColor: "#fdecec", color: "#8b1c1c" },
  badgeSoft: { backgroundColor: "#fef5e3", color: "#a06b16" },
  badgeOk: { backgroundColor: "#e6f6ec", color: "#0e7c3a" },
  badgeInvalid: { backgroundColor: "#eee", color: "#5d5664" },
  pager: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "16px",
    color: BRAND.muted,
    fontSize: "0.85rem",
  },
  bulkBox: {
    backgroundColor: BRAND.bg,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
    marginBottom: "14px",
    display: "flex",
    columnGap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  bulkLabel: {
    fontSize: "0.85rem",
    color: BRAND.ink,
    fontWeight: 600,
    marginRight: "6px",
  },
  matchList: {
    margin: 0,
    paddingLeft: 0,
    listStyleType: "none",
    fontSize: "0.85rem",
  },
  matchItem: {
    paddingTop: "2px",
    paddingBottom: "2px",
  },

  // Native-styled select wrapped in a div so we can keep makeStyles look-and-feel
  selectWrap: {
    position: "relative",
    display: "inline-block",
  },
  select: {
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "12px",
    paddingRight: "30px",
    cursor: "pointer",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("6px"),
    minWidth: "180px",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
    ":focus": {
      outlineStyle: "none",
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  selectChevron: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    color: BRAND.muted,
    pointerEvents: "none",
  },
  empty: {
    color: BRAND.muted,
    fontSize: "0.9rem",
    fontStyle: "italic",
    marginTop: "12px",
  },
  spinnerWrap: { display: "inline-flex", columnGap: "4px", alignItems: "center" },
  spinnerDot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    backgroundColor: BRAND.purple,
    ...shorthands.borderRadius("999px"),
    animationName: {
      "0%, 80%, 100%": { opacity: 0.2 },
      "40%": { opacity: 1 },
    },
    animationDuration: "1.4s",
    animationIterationCount: "infinite",
  },
  spinnerDot2: { animationDelay: "0.16s" },
  spinnerDot3: { animationDelay: "0.32s" },
});

const FILTERS: Array<{ key: ConflictKind | "all"; label: string }> = [
  { key: "all", label: "Alle" },
  { key: "hard", label: "Harte Konflikte" },
  { key: "soft", label: "Weiche Konflikte" },
  { key: "both", label: "Beide" },
  { key: "none", label: "Ohne Konflikt" },
  { key: "invalid", label: "Ungültig" },
];

const PAGE_SIZE = 50;

interface Props {
  batchId: number;
  kind: ImportKind;
}

const labelOfRow = (row: PreviewRow, kind: ImportKind): string => {
  const p: any = row.payload ?? {};
  if (kind === "contacts") {
    const primary = p.primary ?? {};
    const parts = [primary.lastname, primary.firstName].filter(Boolean);
    if (parts.length === 0 && primary.contactNbrExt) {
      return `Kunde ${primary.contactNbrExt}`;
    }
    return parts.join(", ") || `Zeile ${row.rowIndex + 1}`;
  }
  return p.contractNbr ? `Vertrag ${p.contractNbr}` : `Zeile ${row.rowIndex + 1}`;
};

const matchLabel = (m: any, kind: ImportKind): string => {
  if (!m) return "—";
  if (kind === "contacts") {
    const parts = [m.lastname, m.firstName].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : `#${m.id}`;
  }
  return m.contractNbr ? `Vertrag ${m.contractNbr}` : `#${m.id}`;
};

const decisionsForKind = (
  kind: ConflictKind,
  importKind: ImportKind,
): RowDecisionAction[] => {
  if (kind === "invalid") return ["skip"];
  if (kind === "none") return ["create", "skip"];
  if (importKind === "contracts") return ["skip", "update", "force_create"];
  if (kind === "hard") return ["skip", "update", "merge", "force_create"];
  if (kind === "soft") return ["create", "merge", "skip"];
  return ["skip", "update", "merge", "force_create"];
};

const decisionLabel: Record<RowDecisionAction, string> = {
  create: "Neu anlegen",
  skip: "Überspringen",
  update: "Felder aktualisieren",
  merge: "Zusammenführen…",
  force_create: "Trotzdem neu anlegen",
};

const Spinner3: React.FC = () => {
  const styles = useStyles();
  return (
    <span className={styles.spinnerWrap} aria-label="Lade…">
      <span className={styles.spinnerDot} />
      <span className={mergeClasses(styles.spinnerDot, styles.spinnerDot2)} />
      <span className={mergeClasses(styles.spinnerDot, styles.spinnerDot3)} />
    </span>
  );
};

export const ImportReviewStep: React.FC<Props> = ({ batchId, kind }) => {
  const styles = useStyles();
  const [filter, setFilter] = useState<ConflictKind | "all">("hard");
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mergeRow, setMergeRow] = useState<PreviewRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await importService.listRows(batchId, filter, PAGE_SIZE, offset);
      setRows(res.rows);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOffset(0);
  }, [filter]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, filter, offset]);

  const onChangeDecision = async (row: PreviewRow, action: RowDecisionAction) => {
    if (action === "merge") {
      setMergeRow(row);
      return;
    }
    const decision: RowDecision = {
      action,
      targetId: row.matchIds[0],
    };
    await importService.setDecision(batchId, row.rowId, decision);
    setRows((prev) =>
      prev.map((r) => (r.rowId === row.rowId ? { ...r, decision } : r)),
    );
  };

  const onBulk = async (action: RowDecisionAction) => {
    if (filter === "all" || action === "merge") return;
    await importService.bulkDecision(batchId, filter, { action });
    await load();
  };

  const renderBadge = (k: ConflictKind) => {
    if (k === "hard" || k === "both")
      return (
        <span className={mergeClasses(styles.badgeBase, styles.badgeHard)}>
          <Warning20Regular style={{ fontSize: 13 }} /> Hart
        </span>
      );
    if (k === "soft")
      return (
        <span className={mergeClasses(styles.badgeBase, styles.badgeSoft)}>
          <Warning20Regular style={{ fontSize: 13 }} /> Weich
        </span>
      );
    if (k === "invalid")
      return (
        <span className={mergeClasses(styles.badgeBase, styles.badgeInvalid)}>
          ungültig
        </span>
      );
    return (
      <span className={mergeClasses(styles.badgeBase, styles.badgeOk)}>
        <CheckmarkCircle20Filled style={{ fontSize: 13 }} /> OK
      </span>
    );
  };

  const onMergeSaved = async () => {
    setMergeRow(null);
    await load();
  };

  return (
    <div>
      <div className={styles.filterBar}>
        <Filter20Regular className={styles.filterIcon} />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={mergeClasses(styles.pill, (filter === f.key) && styles.pillActive)}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filter !== "all" && (
        <div className={styles.bulkBox}>
          <span className={styles.bulkLabel}>Massenaktion:</span>
          {(filter === "hard" || filter === "both") && (
            <>
              <button type="button" className={styles.pill} onClick={() => onBulk("skip")}>
                Alle überspringen
              </button>
              {kind === "contacts" && (
                <button type="button" className={styles.pill} onClick={() => onBulk("update")}>
                  Alle Felder aktualisieren
                </button>
              )}
            </>
          )}
          {filter === "soft" && (
            <>
              <button type="button" className={styles.pill} onClick={() => onBulk("create")}>
                Alle neu anlegen
              </button>
              <button type="button" className={styles.pill} onClick={() => onBulk("skip")}>
                Alle überspringen
              </button>
            </>
          )}
          {filter === "none" && (
            <>
              <button type="button" className={styles.pill} onClick={() => onBulk("create")}>
                Alle neu anlegen
              </button>
              <button type="button" className={styles.pill} onClick={() => onBulk("skip")}>
                Alle überspringen
              </button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <Spinner3 />
      ) : rows.length === 0 ? (
        <p className={styles.empty}>Keine Zeilen für diesen Filter.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>#</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Zeile aus Excel</th>
              <th className={styles.th}>Treffer in DB</th>
              <th className={styles.th}>Entscheidung</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const allowed = decisionsForKind(r.conflictKind, kind);
              return (
                <tr key={r.rowId}>
                  <td className={styles.td} style={{ color: BRAND.muted }}>
                    {r.rowIndex + 1}
                  </td>
                  <td className={styles.td}>{renderBadge(r.conflictKind)}</td>
                  <td className={styles.td}>
                    <strong>{labelOfRow(r, kind)}</strong>
                    {r.reasons.length > 0 && (
                      <div className={styles.reasons}>
                        {r.reasons.map((rs, i) => (
                          <span key={i}>
                            {formatReason(rs)}
                            {i < r.reasons.length - 1 ? " · " : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className={styles.td}>
                    {(r.matches ?? []).length > 0 ? (
                      <ul className={styles.matchList}>
                        {(r.matches ?? []).map((m: any) => (
                          <li key={m.id} className={styles.matchItem}>
                            #{m.id} — {matchLabel(m, kind)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span style={{ color: BRAND.muted }}>—</span>
                    )}
                  </td>
                  <td className={styles.td}>
                    <span className={styles.selectWrap}>
                      <select
                        className={styles.select}
                        value={r.decision?.action ?? ""}
                        onChange={(e) =>
                          onChangeDecision(r, e.target.value as RowDecisionAction)
                        }
                      >
                        {!r.decision?.action && <option value="">— wählen —</option>}
                        {allowed.map((a) => (
                          <option key={a} value={a}>
                            {decisionLabel[a]}
                          </option>
                        ))}
                      </select>
                      <ChevronDown20Regular className={styles.selectChevron} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {total > PAGE_SIZE && (
        <div className={styles.pager}>
          <span>
            {offset + 1} – {Math.min(offset + PAGE_SIZE, total)} von {total}
          </span>
          <div style={{ display: "flex", columnGap: 8 }}>
            <button
              type="button"
              className={styles.pill}
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              <ArrowLeft20Regular style={{ fontSize: 14 }} /> Zurück
            </button>
            <button
              type="button"
              className={styles.pill}
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Weiter <ArrowRight20Regular style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>
      )}

      {mergeRow && (
        <ImportMergeDrawer
          batchId={batchId}
          row={mergeRow}
          onClose={() => setMergeRow(null)}
          onSaved={onMergeSaved}
        />
      )}
    </div>
  );
};
